import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getAllParties } from "../slices/partySlice";
import { getAllProducts } from "../slices/productSlice";
import {
  getAllBills,
  createBill,
  getBillById,
  updateBill,
  deleteBill,
  resetCreate,
  resetUpdate,
} from "../slices/billSlice";
import { usePermissions } from "../utils/permissions";
import PermissionGate from "../componets/PermissionGate";
import BottomNavbar from "../componets/BottomNavbar";
import businessService from "../services/businessService";
import billService from "../services/billService";
import whatsappApi from "../services/api";
import { generateBillPdf, generateInvoiceHTML } from "../services/billPdfGenerator";
import { getLineTotal, getQuantityFromAmount, applyGrandTotalToItems } from "../utils/billLineCalc";

const BillManagement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isAuthenticated } = useSelector((state) => state.user);
  const { activeRoles } = useSelector((state) => state.role);
  const { parties } = useSelector((state) => state.party);
  const { products } = useSelector((state) => state.product);
  const {
    bills,
    loading,
    createLoading,
    updateLoading,
    createSuccess,
    updateSuccess,
  } = useSelector((state) => state.bill);

  usePermissions();

  // Check permission
  const canReadBills = useMemo(() => {
    if (user?.role === "admin") return true;
    const userRole = activeRoles.find((role) => role.name === user?.role);
    if (!userRole) return false;
    const permission = userRole.permissions.find((p) => p.resource === "parties");
    return permission && permission.actions.includes("read");
  }, [user?.role, activeRoles]);

  // Check if user can create bills (has parties create permission or is admin)
  const canCreateBills = useMemo(() => {
    if (user?.role === "admin") return true;
    const userRole = activeRoles.find((role) => role.name === user?.role);
    if (!userRole) return false;
    const permission = userRole.permissions.find((p) => p.resource === "parties");
    return permission && permission.actions.includes("create");
  }, [user?.role, activeRoles]);

  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteBillId, setDeleteBillId] = useState(null);
  const [whatsappModal, setWhatsappModal] = useState({ open: false, bill: null, phone: "" });
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  const [pdfPreviewModal, setPdfPreviewModal] = useState({ open: false, bill: null, blobUrl: null, blob: null, htmlContent: "" });
  const pdfPreviewIframeRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [editingBillId, setEditingBillId] = useState(null);

  // Bill form data
  const [formData, setFormData] = useState({
    partyId: "",
    billDate: new Date().toISOString().split("T")[0],
    billNumber: "",
    billingName: "",
    billType: "withoutTax",
    status: "pending",
  });
  const [customerSearch, setCustomerSearch] = useState("");
  const [productSearch, setProductSearch] = useState("");
  const [showPartyDropdown, setShowPartyDropdown] = useState(false);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [amountDraft, setAmountDraft] = useState(null);
  const [lineAmountDrafts, setLineAmountDrafts] = useState({});
  const [saveAndNewRequested, setSaveAndNewRequested] = useState(false);
  const [createStep, setCreateStep] = useState(1);
  const productInputRef = useRef(null);
  const lastSavedInvoiceRef = useRef("");

  // Bill items
  const [billItems, setBillItems] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [selectedRate, setSelectedRate] = useState(0);
  const [selectedGstRate, setSelectedGstRate] = useState(18); // Default Bill: 100 + 18% = 118

  // Calculated totals
  // With Tax: rate = price inclusive of GST → total = rate×qty, base = total/(1+GST%), GST = total - base
  // Without Tax: rate = base price → base = rate×qty, GST = base×GST%, total = base + GST
  const totals = useMemo(() => {
    let subtotal = 0;
    let totalGst = 0;
    const billType = formData.billType || "withoutTax";
    const isKachha = billType === "kachuBill" || billType === "kachuBillGstIncl" || billType === "delivery";

    billItems.forEach((item) => {
      const rate = item.rate || 0;
      const qty = item.quantity || 0;
      const gstRate = isKachha ? 0 : (item.gstRate || 0);
      let itemBase, itemGst, itemTotal;

      if (isKachha) {
        itemTotal = rate * qty;
        itemBase = itemTotal;
        itemGst = 0;
      } else if (billType === "withTax" || billType === "withTaxIgst") {
        itemTotal = rate * qty;
        itemBase = gstRate > 0 ? itemTotal / (1 + gstRate / 100) : itemTotal;
        itemGst = itemTotal - itemBase;
      } else {
        itemBase = rate * qty;
        itemGst = (itemBase * gstRate) / 100;
        itemTotal = itemBase + itemGst;
      }

      subtotal += itemBase;
      totalGst += itemGst;
    });

    const totalAmount = subtotal + totalGst;
    const roundOff = Math.round(totalAmount) - totalAmount;
    const grandTotal = Math.round(totalAmount);

    return {
      subtotal: parseFloat(subtotal.toFixed(2)),
      totalGst: parseFloat(totalGst.toFixed(2)),
      totalAmount: parseFloat(totalAmount.toFixed(2)),
      roundOff: parseFloat(roundOff.toFixed(2)),
      grandTotal,
    };
  }, [billItems, formData.billType]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }

    if (!canReadBills) {
      navigate("/my-business");
      return;
    }

    // Load parties, products, and bills
    dispatch(getAllParties({ page: 1, limit: 100 }));
    dispatch(getAllProducts({ page: 1, limit: 100 }));
    dispatch(getAllBills());
  }, [dispatch, isAuthenticated, navigate, canReadBills]);

  // Preselect party from URL (e.g. /bills?partyId=xxx from PartyManagement Take Bill)
  useEffect(() => {
    const partyIdFromUrl = searchParams.get("partyId");
    if (partyIdFromUrl && parties?.length > 0) {
      const party = parties.find((p) => p._id === partyIdFromUrl);
      if (party) {
        const name = party.partyName || party.companyName || "";
        setFormData((prev) => ({ ...prev, partyId: partyIdFromUrl, billingName: name }));
        setCustomerSearch(name);
        if (canCreateBills) setIsModalOpen(true);
        setSearchParams({});
      }
    }
  }, [searchParams, parties, canCreateBills, setSearchParams]);

  // Handle create success
  useEffect(() => {
    if (createSuccess) {
      if (saveAndNewRequested) {
        setCreateStep(1);
        setFormData({
          partyId: "",
          billDate: new Date().toISOString().split("T")[0],
          billNumber: lastSavedInvoiceRef.current || "",
          billingName: "",
          billType: "withoutTax",
          status: "pending",
        });
        setBillItems([]);
        setSelectedProductId("");
        setSelectedQuantity(1);
        setSelectedRate(0);
        setSelectedGstRate(18);
        setCustomerSearch("");
        setProductSearch("");
        dispatch(getAllBills());
      } else {
        setIsModalOpen(false);
        setFormData({
          partyId: "",
          billDate: new Date().toISOString().split("T")[0],
          billNumber: "",
          billingName: "",
          billType: "withoutTax",
          status: "pending",
        });
        setBillItems([]);
      }
      setSaveAndNewRequested(false);
      dispatch(resetCreate());
    }
  }, [createSuccess, dispatch, saveAndNewRequested]);

  // Handle update success
  useEffect(() => {
    if (updateSuccess) {
      dispatch(resetUpdate());
      // Reload bills to show updated PDF
      dispatch(getAllBills());
    }
  }, [updateSuccess, dispatch]);

  const handlePartyChange = (partyId) => {
    const party = parties.find((p) => p._id === partyId);
    setFormData((prev) => ({
      ...prev,
      partyId,
      billingName: party ? (party.partyName || party.companyName || "") : prev.billingName,
    }));
    setCustomerSearch(party ? (party.partyName || party.companyName || "") : "");
    setShowPartyDropdown(false);
  };

  const filteredParties = useMemo(() => {
    const active = parties.filter((p) => p.isActive !== false);
    if (!customerSearch.trim()) return active.slice(0, 20);
    const q = customerSearch.toLowerCase().trim();
    return active.filter((p) => (p.partyName || "").toLowerCase().includes(q) || (p.companyName || "").toLowerCase().includes(q) || (p.mobileNumber || "").includes(q)).slice(0, 20);
  }, [parties, customerSearch]);

  const filteredProducts = useMemo(() => {
    const active = products.filter((p) => p.isActive !== false);
    if (!productSearch.trim()) return active.slice(0, 20);
    const q = productSearch.toLowerCase().trim();
    return active.filter((p) => (p.name || "").toLowerCase().includes(q) || (p.hsnCode || "").toLowerCase().includes(q)).slice(0, 20);
  }, [products, productSearch]);

  const selectedParty = useMemo(() => parties.find((p) => p._id === formData.partyId), [parties, formData.partyId]);

  const selectedLineAmount = useMemo(
    () =>
      getLineTotal({
        rate: selectedRate,
        quantity: selectedQuantity,
        gstRate: selectedGstRate,
        billType: formData.billType,
      }),
    [selectedRate, selectedQuantity, selectedGstRate, formData.billType]
  );

  const applySelectedAmount = (value) => {
    const amount = value === "" ? 0 : parseFloat(value) || 0;
    if (selectedRate > 0 && amount > 0) {
      setSelectedQuantity(
        getQuantityFromAmount({
          amount,
          rate: selectedRate,
          gstRate: selectedGstRate,
          billType: formData.billType,
        })
      );
    } else if (value === "") {
      setSelectedQuantity(0);
    }
  };

  const selectDropdownItem = (handler) => (e) => {
    e.preventDefault();
    handler();
  };

  const defaultGstFromProduct = (product) => {
    if (!product) return 18;
    const g = product.gstRate;
    if (g === null || g === undefined || g === "") return 18;
    const n = Number(g);
    return !Number.isNaN(n) && n >= 0 ? n : 18;
  };

  const handleProductSelect = (productId) => {
    const product = products.find((p) => p._id === productId);
    if (product) {
      setSelectedProductId(productId);
      setSelectedRate(product.price);
      setSelectedQuantity(1);
      if (formData.billType === "kachuBill" || formData.billType === "kachuBillGstIncl") setSelectedGstRate(0);
      else setSelectedGstRate(defaultGstFromProduct(product));
      setProductSearch(product.name || "");
      setShowProductDropdown(false);
      setAmountDraft(null);
    }
  };

  const handleAddProduct = () => {
    const errors = [];
    if (!selectedProductId) errors.push("Product");
    const product = products.find((p) => p._id === selectedProductId);
    if (selectedProductId && !product) errors.push("Product (not found)");
    if (selectedQuantity <= 0 || selectedQuantity === undefined || selectedQuantity === null) errors.push("Qty (must be > 0)");
    const rate = Number(selectedRate);
    if (rate === 0 || rate === undefined || isNaN(rate)) errors.push("Rate");

    if (errors.length > 0) {
      toast.error("Please fill: " + errors.join(", "));
      return;
    }

    // Check if product already exists in bill items
    const existingItemIndex = billItems.findIndex(
      (item) => item.productId === selectedProductId
    );

    if (existingItemIndex >= 0) {
      // Update existing item
      const updatedItems = [...billItems];
      updatedItems[existingItemIndex] = {
        ...updatedItems[existingItemIndex],
        quantity: selectedQuantity,
        rate: selectedRate,
        gstRate: selectedGstRate,
      };
      setBillItems(updatedItems);
    } else {
      // Add new item
      setBillItems([
        ...billItems,
        {
          productId: selectedProductId,
          productName: product.name,
          hsnCode: product.hsnCode,
          unit: product.unit,
          quantity: selectedQuantity,
          rate: selectedRate,
          gstRate: selectedGstRate,
        },
      ]);
    }

    setSelectedProductId("");
    setSelectedQuantity(1);
    setSelectedRate(0);
    setProductSearch("");
    setShowProductDropdown(false);
    setAmountDraft(null);
    if (formData.billType === "kachuBill" || formData.billType === "kachuBillGstIncl") setSelectedGstRate(0);
    else setSelectedGstRate(18);
    setTimeout(() => productInputRef.current?.focus(), 100);
  };

  const handleRemoveItem = (index) => {
    setBillItems(billItems.filter((_, i) => i !== index));
  };

  const handleGrandTotalChange = (value) => {
    const target = value === "" ? 0 : parseFloat(value) || 0;
    if (billItems.length === 0) return;
    if (target <= 0) {
      // Allow user to clear total to 0 (sets all quantities to 0)
      setBillItems((prev) => prev.map((it) => ({ ...it, quantity: 0 })));
      return;
    }
    setBillItems(applyGrandTotalToItems(billItems, target, formData.billType));
  };

  // Inline edit: quantity, rate, gstRate, or line amount (amount → auto qty)
  const handleUpdateItem = (index, field, value) => {
    const isEmpty = value === "";
    const num = parseFloat(value);
    setBillItems((prev) => {
      const next = [...prev];
      if (!next[index]) return prev;
      const item = next[index];
      const billType = formData.billType || "withoutTax";
      if (field === "quantity") {
        next[index] = { ...item, quantity: isEmpty ? 0 : (!isNaN(num) && num >= 0 ? num : item.quantity) };
      } else if (field === "rate") {
        next[index] = { ...item, rate: isEmpty ? 0 : (!isNaN(num) && num >= 0 ? num : item.rate) };
      } else if (field === "gstRate") {
        next[index] = { ...item, gstRate: isEmpty ? 0 : (!isNaN(num) && num >= 0 ? num : item.gstRate) };
      } else if (field === "lineAmount") {
        const rate = item.rate || 0;
        const amount = isEmpty ? 0 : !isNaN(num) && num >= 0 ? num : 0;
        if (rate > 0 && amount >= 0) {
          const isKachha =
            billType === "kachuBill" || billType === "kachuBillGstIncl" || billType === "delivery";
          const qty = getQuantityFromAmount({
            amount,
            rate,
            gstRate: isKachha ? 0 : item.gstRate || 0,
            billType,
          });
          next[index] = { ...item, quantity: qty };
        }
      }
      return next;
    });
  };

  const loadNextInvoiceNumber = async () => {
    try {
      const res = await billService.getNextInvoiceNumber();
      const next = res?.nextInvoiceNumber || "";
      if (next) setFormData((p) => ({ ...p, billNumber: next }));
    } catch {
      /* keep current */
    }
  };

  const handleOpenModal = async () => {
    setEditingBillId(null);
    setIsModalOpen(true);
    setCreateStep(1);
    setFormData({
      partyId: "",
      billDate: new Date().toISOString().split("T")[0],
      billNumber: "",
      billingName: "",
      billType: "withoutTax",
      status: "pending",
    });
    setBillItems([]);
    setSelectedProductId("");
    setSelectedQuantity(1);
    setSelectedRate(0);
    setSelectedGstRate(18);
    setCustomerSearch("");
    setProductSearch("");
    setShowPartyDropdown(false);
    setShowProductDropdown(false);
    await loadNextInvoiceNumber();
  };

  const handleEditBill = async (bill) => {
    try {
      const full = await dispatch(getBillById(bill._id)).unwrap();
      const partyId = full.partyId?._id || full.partyId;
      const partyName = full.party?.partyName || full.party?.companyName || "";
      const billDate = full.billDate ? new Date(full.billDate).toISOString().split("T")[0] : new Date().toISOString().split("T")[0];
      const bt = full.billType;
      const isKachu = bt === "kachuBill" || bt === "kachuBillGstIncl" || bt === "delivery";
      const billType = isKachu ? "kachuBill" : (bt === "withTaxIgst" || bt === "withoutTaxIgst" ? "withoutTaxIgst" : "withoutTax");
      const rawFirstGst = full.items?.[0]?.gstRate;
      const gstRate =
        rawFirstGst != null && rawFirstGst !== ""
          ? Number(rawFirstGst)
          : 18;
      setFormData({
        partyId,
        billDate,
        billNumber: full.billNumber || "",
        billingName: full.billingName || partyName,
        billType,
        status: full.status || "pending",
      });
      setCustomerSearch(partyName);
      const items = (full.items || []).map((item) => ({
        productId: item.productId?._id || item.productId,
        productName: item.productName,
        hsnCode: item.hsnCode || "",
        unit: item.unit || "",
        quantity: item.quantity,
        rate: item.rate,
        gstRate:
          item.gstRate != null && item.gstRate !== ""
            ? Number(item.gstRate)
            : isKachu
              ? 0
              : gstRate,
      }));
      setBillItems(items);
      setSelectedProductId("");
      setSelectedQuantity(1);
      setSelectedRate(0);
      setSelectedGstRate(isKachu ? 0 : gstRate);
      setProductSearch("");
      setShowPartyDropdown(false);
      setShowProductDropdown(false);
      setEditingBillId(bill._id);
      setCreateStep(2);
      setIsModalOpen(true);
    } catch (error) {
      toast.error(error?.message || "Failed to load bill for editing");
    }
  };

  const handleCloseModal = (keepOpenForNew = false) => {
    if (!keepOpenForNew) {
      setIsModalOpen(false);
      setEditingBillId(null);
    }
    setCreateStep(1);
    setFormData({
      partyId: "",
      billDate: new Date().toISOString().split("T")[0],
      billNumber: "",
      billingName: "",
      billType: "withoutTax",
      status: "pending",
    });
    setBillItems([]);
    setSelectedProductId("");
    setSelectedQuantity(1);
    setSelectedRate(0);
    setSelectedGstRate(18);
    setCustomerSearch("");
    setProductSearch("");
    if (keepOpenForNew) {
      setShowPartyDropdown(true);
    }
  };

  const handleCreateBill = async (saveAndNew = false) => {
    if (!formData.partyId) {
      toast.error("Please select a party");
      return;
    }

    if (billItems.length === 0) {
      toast.error("Please add at least one product");
      return;
    }

    const billData = {
      partyId: formData.partyId,
      items: billItems.map((item) => ({
        productId: item.productId,
        quantity: item.quantity,
        rate: item.rate,
        gstRate: (formData.billType === "kachuBill" || formData.billType === "kachuBillGstIncl") ? 0 : (item.gstRate ?? 18),
      })),
      billDate: formData.billDate,
      status: formData.status,
      notes: "",
      billNumber: formData.billNumber?.trim() || undefined,
      billType: formData.billType || undefined,
      billingName: formData.billingName?.trim() || undefined,
    };

    if (editingBillId) {
      try {
        await dispatch(updateBill({ billId: editingBillId, billData })).unwrap();
        setIsModalOpen(false);
        setEditingBillId(null);
        dispatch(getAllBills());
      } catch (error) {
        console.error("Error updating bill:", error);
      }
      return;
    }

    setSaveAndNewRequested(saveAndNew);
    lastSavedInvoiceRef.current = formData.billNumber?.trim() || "";

    try {
      await dispatch(createBill(billData)).unwrap();
    } catch (error) {
      console.error("Error creating bill:", error);
      // Error toast is handled by the slice
    }
  };

  const handleViewBill = async (bill) => {
    const openPdfInPopup = async (billId, billForTitle) => {
      const [fullBillRes, businessRes] = await Promise.all([
        dispatch(getBillById(billId)).unwrap(),
        businessService.getMyBusiness().catch(() => ({ business: null })),
      ]);
      const fullBill = fullBillRes?.bill ?? fullBillRes ?? billForTitle ?? bill;
      const business = businessRes?.business ?? null;
      const htmlContent = generateInvoiceHTML(fullBill, business);

      // Open modal immediately with HTML so correct type/design shows (Kachha, With Tax, etc.)
      setPdfPreviewModal({
        open: true,
        bill: billForTitle || bill,
        blobUrl: null,
        blob: null,
        htmlContent,
      });

      // Fetch PDF blob in background for Download / Open in new tab (do not block view)
      let blob = null;
      try {
        blob = await billService.getBillPdfBlob(billId);
      } catch (err) {
        const status = err?.status ?? err?.response?.status;
        const msg = err?.message || "";
        const is404 = status === 404 || String(msg).toLowerCase().includes("not found");
        if (is404) {
          toast.info("PDF not on server, generating...");
          try {
            await generateBillPdf(fullBill._id);
            blob = await billService.getBillPdfBlob(billId);
          } catch (retryErr) {
            console.warn("PDF blob still unavailable after regenerate:", retryErr);
          }
        }
      }
      if (blob) {
        const blobUrl = URL.createObjectURL(blob);
        setPdfPreviewModal((prev) =>
          prev.open && prev.bill?._id === billId ? { ...prev, blobUrl, blob } : prev
        );
      }
    };

    try {
      toast.info("Opening bill...");
      await openPdfInPopup(bill._id, bill);
    } catch (error) {
      const msg = error?.message || error?.response?.data?.message || "Failed to open bill";
      toast.error(msg);
    }
  };

  const closePdfPreviewModal = () => {
    if (pdfPreviewModal.blobUrl) URL.revokeObjectURL(pdfPreviewModal.blobUrl);
    setPdfPreviewModal({ open: false, bill: null, blobUrl: null, blob: null, htmlContent: "" });
  };

  const handleDownloadPdf = () => {
    if (!pdfPreviewModal.blob || !pdfPreviewModal.bill) return;
    const name = (pdfPreviewModal.bill.billNumber || "bill").replace(/[^a-zA-Z0-9]/g, "_") + ".pdf";
    const a = document.createElement("a");
    a.href = URL.createObjectURL(pdfPreviewModal.blob);
    a.download = name;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const handleOpenPdfInNewTab = () => {
    if (!pdfPreviewModal.blobUrl) return;
    window.open(pdfPreviewModal.blobUrl, "_blank", "noopener");
  };

  const handleDeleteClick = (billId) => {
    setDeleteBillId(billId);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteBillId) return;

    try {
      await dispatch(deleteBill(deleteBillId)).unwrap();
      setIsDeleteModalOpen(false);
      setDeleteBillId(null);
      // Bill is automatically removed from Redux state by the slice
    } catch (error) {
      console.error("Error deleting bill:", error);
      // Error toast is handled by the slice
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setDeleteBillId(null);
  };

  const openWhatsappModal = (bill) => {
    const phone = (bill.party?.mobileNumber || "").replace(/\D/g, "").slice(-10);
    setWhatsappModal({ open: true, bill, phone });
  };

  const closeWhatsappModal = () => {
    setWhatsappModal({ open: false, bill: null, phone: "" });
  };

  const handleSendWhatsAppBill = async () => {
    let { bill, phone } = whatsappModal;
    if (!bill?._id) {
      toast.error("No bill selected.");
      return;
    }
    const num = (phone || "").replace(/\D/g, "").slice(-10);
    if (num.length !== 10) {
      toast.error("Enter a valid 10-digit phone number.");
      return;
    }
    setSendingWhatsApp(true);
    const partyLabelRaw =
      bill?.party?.companyName || bill?.party?.partyName || "Customer";
    const safePartyLabel = String(partyLabelRaw)
      .trim()
      .replace(/[\\/:*?"<>|]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 80);
    const desiredFileName = `${safePartyLabel || "Customer"}.pdf`;
    const sendWithPath = async (filePath) => {
      await whatsappApi.post("/wp/send", {
        number: num,
        filePath: filePath.replace(/^\//, ""),
        message: bill.billNumber || "",
        fileName: desiredFileName,
      });
    };
    try {
      let filePathToSend = bill.pdfFile?.fileUrl;
      let shouldGenerate = !filePathToSend;
      if (filePathToSend) {
        try {
          await sendWithPath(filePathToSend);
          toast.success("Bill sent to WhatsApp.");
          closeWhatsappModal();
          return;
        } catch (err) {
          const data = err?.response?.data;
          const status = err?.response?.status;
          const resMsg = (data && typeof data === "object" ? data.message : data) || err?.message || "";
          const isFileNotFound =
            status === 404 ||
            (data && typeof data === "object" && data.code === "FILE_NOT_FOUND") ||
            /file not found/i.test(String(resMsg));
          if (!isFileNotFound) throw err;
          shouldGenerate = true;
        }
      }
      if (shouldGenerate) {
        toast.info("Generating PDF, then sending...");
        const fullBillRes = await dispatch(getBillById(bill._id)).unwrap();
        const fullBill = fullBillRes?.bill ?? fullBillRes;
        if (!fullBill?.items?.length) {
          toast.error("Bill has no items. Cannot generate PDF.");
          return;
        }
        await generateBillPdf(fullBill._id);
        const updatedRes = await dispatch(getBillById(bill._id)).unwrap();
        const updated = updatedRes?.bill ?? updatedRes;
        filePathToSend = updated?.pdfFile?.fileUrl;
        if (!filePathToSend) {
          toast.error("PDF could not be generated. Open the bill and click View, then try again.");
          return;
        }
        await sendWithPath(filePathToSend);
        toast.success("Bill sent to WhatsApp.");
        closeWhatsappModal();
      }
    } catch (err) {
      const data = err?.response?.data;
      const msg = (data && typeof data === "object" ? data.message : data) || err?.message || "Failed to send to WhatsApp.";
      toast.error(msg);
    } finally {
      setSendingWhatsApp(false);
    }
  };

  // Filter bills
  const filteredBills = useMemo(() => {
    if (!searchTerm.trim()) return bills;

    const searchLower = searchTerm.toLowerCase();
    return bills.filter(
      (bill) =>
        bill.billNumber?.toLowerCase().includes(searchLower) ||
        bill.party?.companyName?.toLowerCase().includes(searchLower) ||
        bill.party?.partyName?.toLowerCase().includes(searchLower) ||
        bill.party?.mobileNumber?.includes(searchTerm)
    );
  }, [bills, searchTerm]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const styles = {
    container: {
      minHeight: "100vh",
      backgroundColor: "#f0f2f5",
      paddingBottom: isMobile ? "90px" : "100px",
    },
    header: {
      backgroundColor: "#ffffff",
      padding: isMobile ? "16px 16px 12px" : "20px 24px 16px",
      borderBottom: "1px solid #e9edef",
      position: "sticky",
      top: 0,
      zIndex: 100,
      boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    },
    headerTitle: {
      fontSize: isMobile ? "24px" : "28px",
      fontWeight: "600",
      color: "#111b21",
      marginBottom: "8px",
      letterSpacing: "-0.01em",
    },
    headerSubtitle: {
      fontSize: "14px",
      color: "#667781",
      fontWeight: "400",
    },
    searchContainer: {
      padding: isMobile ? "12px 16px" : "16px 24px",
      backgroundColor: "#ffffff",
      borderBottom: "1px solid #e9edef",
    },
    searchInput: {
      width: "100%",
      padding: isMobile ? "10px 16px" : "12px 20px",
      fontSize: "15px",
      border: "1px solid #e9edef",
      borderRadius: "8px",
      backgroundColor: "#f0f2f5",
      outline: "none",
      transition: "border-color 0.2s",
    },
    content: {
      padding: isMobile ? "16px" : "24px",
    },
    billsList: {
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(350px, 1fr))",
      gap: isMobile ? "12px" : "16px",
    },
    billCard: {
      backgroundColor: "#ffffff",
      borderRadius: "8px",
      padding: isMobile ? "16px" : "20px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
      transition: "box-shadow 0.2s, transform 0.2s",
      border: "1px solid #e9edef",
    },
    billHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      marginBottom: "12px",
    },
    billNumber: {
      fontSize: "16px",
      fontWeight: "600",
      color: "#111b21",
      flex: 1,
      marginRight: "12px",
    },
    billDate: {
      fontSize: "13px",
      color: "#667781",
      whiteSpace: "nowrap",
    },
    billParty: {
      fontSize: "14px",
      color: "#111b21",
      marginBottom: "8px",
      fontWeight: "500",
    },
    billMeta: {
      fontSize: "12px",
      color: "#667781",
      marginBottom: "4px",
    },
    billTotals: {
      marginTop: "12px",
      paddingTop: "12px",
      borderTop: "1px solid #f0f2f5",
    },
    billTotalRow: {
      display: "flex",
      justifyContent: "space-between",
      fontSize: "13px",
      marginBottom: "4px",
    },
    billGrandTotal: {
      display: "flex",
      justifyContent: "space-between",
      fontSize: "16px",
      fontWeight: "600",
      color: "#111b21",
      marginTop: "8px",
      paddingTop: "8px",
      borderTop: "1px solid #e9edef",
    },
    billStatus: {
      display: "inline-block",
      padding: "4px 8px",
      borderRadius: "4px",
      fontSize: "11px",
      fontWeight: "500",
      marginTop: "8px",
    },
    statusPending: {
      backgroundColor: "#fff3cd",
      color: "#856404",
    },
    statusPaid: {
      backgroundColor: "#d4edda",
      color: "#155724",
    },
    statusCancelled: {
      backgroundColor: "#f8d7da",
      color: "#721c24",
    },
    billActions: {
      display: "flex",
      gap: "8px",
      marginTop: "12px",
      paddingTop: "12px",
      borderTop: "1px solid #f0f2f5",
    },
    actionButton: {
      flex: 1,
      padding: isMobile ? "8px 12px" : "10px 16px",
      fontSize: "13px",
      fontWeight: "500",
      border: "none",
      borderRadius: "6px",
      cursor: "pointer",
      transition: "background-color 0.2s",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "6px",
    },
    viewButton: {
      backgroundColor: "#25d366",
      color: "#ffffff",
    },
    deleteButton: {
      backgroundColor: "#f15c6d",
      color: "#ffffff",
    },
    emptyState: {
      textAlign: "center",
      padding: isMobile ? "60px 20px" : "80px 40px",
      color: "#667781",
    },
    emptyStateIcon: {
      fontSize: "64px",
      marginBottom: "16px",
    },
    emptyStateText: {
      fontSize: "16px",
      fontWeight: "400",
      marginBottom: "8px",
    },
    emptyStateSubtext: {
      fontSize: "14px",
      color: "#8696a0",
    },
    loadingState: {
      textAlign: "center",
      padding: "60px 20px",
      color: "#667781",
      fontSize: "16px",
    },
    // FAB styles
    fab: {
      position: "fixed",
      bottom: isMobile ? "90px" : "100px",
      right: isMobile ? "20px" : "32px",
      width: isMobile ? "56px" : "64px",
      height: isMobile ? "56px" : "64px",
      borderRadius: "50%",
      backgroundColor: "#25d366",
      color: "#ffffff",
      border: "none",
      boxShadow: "0 4px 12px rgba(37, 211, 102, 0.4)",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "28px",
      zIndex: 1001,
      transition: "transform 0.2s, box-shadow 0.2s",
    },
    // Modal styles
    modalOverlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1002,
      padding: isMobile ? "16px" : "24px",
      overflowY: "auto",
    },
    modalContent: {
      backgroundColor: "#ffffff",
      borderRadius: "8px",
      padding: isMobile ? "20px 16px" : "32px 24px",
      maxWidth: isMobile ? "100%" : "800px",
      width: "100%",
      maxHeight: "90vh",
      overflowY: "auto",
      boxShadow: "0 8px 32px rgba(0,0,0,0.24)",
      display: "flex",
      flexDirection: "column",
    },
    modalHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "24px",
    },
    modalTitle: {
      fontSize: "20px",
      fontWeight: "600",
      color: "#111b21",
    },
    modalCloseButton: {
      background: "none",
      border: "none",
      fontSize: "24px",
      cursor: "pointer",
      color: "#667781",
      padding: "0",
      width: "32px",
      height: "32px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "50%",
      transition: "background-color 0.2s",
    },
    formGroup: {
      marginBottom: "20px",
    },
    label: {
      display: "block",
      fontSize: "14px",
      fontWeight: "500",
      color: "#111b21",
      marginBottom: "8px",
    },
    input: {
      width: "100%",
      padding: "10px 12px",
      fontSize: "15px",
      border: "1px solid #e9edef",
      borderRadius: "6px",
      backgroundColor: "#ffffff",
      outline: "none",
      transition: "border-color 0.2s",
    },
    select: {
      width: "100%",
      padding: "10px 12px",
      fontSize: "15px",
      border: "1px solid #e9edef",
      borderRadius: "6px",
      backgroundColor: "#ffffff",
      outline: "none",
      transition: "border-color 0.2s",
      cursor: "pointer",
    },
    itemsSection: {
      marginTop: "20px",
      paddingTop: "20px",
      borderTop: "1px solid #e9edef",
    },
    addItemSection: {
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr 1fr 1fr auto",
      gap: "8px",
      marginBottom: "16px",
      alignItems: "end",
    },
    addItemButton: {
      padding: "10px 16px",
      backgroundColor: "#25d366",
      color: "#ffffff",
      border: "none",
      borderRadius: "6px",
      cursor: "pointer",
      fontSize: "13px",
      fontWeight: "500",
      whiteSpace: "nowrap",
    },
    itemsTable: {
      width: "100%",
      borderCollapse: "collapse",
      marginBottom: "20px",
    },
    itemsTableHeader: {
      backgroundColor: "#f0f2f5",
      padding: "10px",
      textAlign: "left",
      fontSize: "12px",
      fontWeight: "600",
      color: "#667781",
      borderBottom: "2px solid #e9edef",
    },
    itemsTableCell: {
      padding: "10px",
      fontSize: "13px",
      color: "#111b21",
      borderBottom: "1px solid #f0f2f5",
    },
    removeItemButton: {
      background: "none",
      border: "none",
      color: "#f15c6d",
      cursor: "pointer",
      fontSize: "16px",
      padding: "4px 8px",
    },
    totalsSection: {
      marginTop: "20px",
      paddingTop: "20px",
      borderTop: "2px solid #e9edef",
    },
    totalRow: {
      display: "flex",
      justifyContent: "space-between",
      padding: "8px 0",
      fontSize: "14px",
      color: "#667781",
    },
    grandTotalRow: {
      display: "flex",
      justifyContent: "space-between",
      padding: "12px 0",
      fontSize: "18px",
      fontWeight: "600",
      color: "#111b21",
      borderTop: "2px solid #e9edef",
      marginTop: "8px",
    },
    textarea: {
      width: "100%",
      padding: "10px 12px",
      fontSize: "15px",
      border: "1px solid #e9edef",
      borderRadius: "6px",
      backgroundColor: "#ffffff",
      outline: "none",
      resize: "vertical",
      minHeight: "80px",
      fontFamily: "inherit",
    },
    modalActions: {
      display: "flex",
      gap: "12px",
      justifyContent: "flex-end",
      marginTop: "24px",
      paddingTop: "20px",
      borderTop: "1px solid #e9edef",
      position: "sticky",
      bottom: 0,
      backgroundColor: "#ffffff",
      zIndex: 1,
      paddingBottom: "20px",
    },
    modalButton: {
      padding: "10px 20px",
      fontSize: "14px",
      fontWeight: "500",
      border: "none",
      borderRadius: "6px",
      cursor: "pointer",
      transition: "background-color 0.2s",
    },
    modalCancelButton: {
      backgroundColor: "#f0f2f5",
      color: "#111b21",
    },
    modalSaveButton: {
      backgroundColor: "#25d366",
      color: "#ffffff",
    },
    deleteModalContent: {
      backgroundColor: "#ffffff",
      borderRadius: "8px",
      padding: isMobile ? "24px 20px" : "32px 24px",
      maxWidth: "400px",
      width: "100%",
      boxShadow: "0 8px 32px rgba(0,0,0,0.24)",
    },
    deleteModalTitle: {
      fontSize: "20px",
      fontWeight: "600",
      color: "#111b21",
      marginBottom: "12px",
    },
    deleteModalMessage: {
      fontSize: "15px",
      color: "#667781",
      marginBottom: "24px",
      lineHeight: "22px",
    },
    deleteModalActions: {
      display: "flex",
      gap: "12px",
      justifyContent: "flex-end",
    },
    deleteModalConfirmButton: {
      backgroundColor: "#f15c6d",
      color: "#ffffff",
    },
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.headerTitle}>Bills</h1>
        <p style={styles.headerSubtitle}>
          {loading ? "Loading..." : `${filteredBills.length} Bill${filteredBills.length !== 1 ? "s" : ""} found`}
        </p>
      </div>

      {/* Search */}
      <div style={styles.searchContainer}>
        <input
          type="text"
          placeholder="Search by bill number, party name, or mobile..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={styles.searchInput}
        />
      </div>

      {/* Content */}
      <div style={styles.content}>
        {loading && bills.length === 0 ? (
          <div style={styles.loadingState}>Loading bills...</div>
        ) : filteredBills.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyStateIcon}>📄</div>
            <div style={styles.emptyStateText}>
              {searchTerm ? "No bills found matching your search" : "No bills found"}
            </div>
            <div style={styles.emptyStateSubtext}>
              {searchTerm
                ? "Try adjusting your search terms"
                : "Create your first bill by clicking the + button"}
            </div>
          </div>
        ) : (
          <div style={styles.billsList}>
            {filteredBills.map((bill) => (
              <div key={bill._id} style={styles.billCard}>
                <div style={styles.billHeader}>
                  <div style={styles.billNumber} title={bill.billNumber}>
                    {bill.billNumber}
                  </div>
                  <div style={styles.billDate}>{formatDate(bill.billDate)}</div>
                </div>

                {bill.party && (
                  <>
                    <div style={styles.billParty}>{bill.party.companyName || bill.party.partyName}</div>
                    {bill.party.mobileNumber && (
                      <div style={styles.billMeta}>📱 {bill.party.mobileNumber}</div>
                    )}
                  </>
                )}

                <div style={styles.billTotals}>
                  <div style={styles.billTotalRow}>
                    <span>Items:</span>
                    <span>{bill.items?.length || 0}</span>
                  </div>
                  <div style={styles.billTotalRow}>
                    <span>Subtotal:</span>
                    <span>₹{bill.subtotal?.toFixed(2) || "0.00"}</span>
                  </div>
                  {bill.totalGst > 0 && (
                    <div style={styles.billTotalRow}>
                      <span>GST:</span>
                      <span>₹{bill.totalGst.toFixed(2)}</span>
                    </div>
                  )}
                  <div style={styles.billGrandTotal}>
                    <span>Total:</span>
                    <span>₹{bill.grandTotal?.toFixed(2) || "0.00"}</span>
                  </div>
                </div>

                <div
                  style={{
                    ...styles.billStatus,
                    ...(bill.status === "paid"
                      ? styles.statusPaid
                      : bill.status === "cancelled"
                      ? styles.statusCancelled
                      : styles.statusPending),
                  }}
                >
                  {bill.status?.toUpperCase() || "PENDING"}
                </div>

                <div style={styles.billActions}>
                  <PermissionGate resource="parties" action="update">
                    <button
                      onClick={() => handleEditBill(bill)}
                      style={{ ...styles.actionButton, backgroundColor: "#0d6efd", color: "#fff" }}
                      title="Edit Order"
                    >
                      ✏️ Edit
                    </button>
                  </PermissionGate>
                  <button
                    onClick={() => handleViewBill(bill)}
                    style={{ ...styles.actionButton, ...styles.viewButton }}
                    title="View/Download Bill PDF"
                  >
                    👁️ View PDF
                  </button>
                  <button
                    onClick={() => openWhatsappModal(bill)}
                    style={{ ...styles.actionButton, backgroundColor: "#25d366", color: "#fff" }}
                    title="Send to WhatsApp"
                  >
                    📱 WhatsApp
                  </button>
                  <PermissionGate resource="parties" action="delete">
                    <button
                      onClick={() => handleDeleteClick(bill._id)}
                      style={{ ...styles.actionButton, ...styles.deleteButton }}
                      title="Delete Bill"
                    >
                      🗑️ Delete
                    </button>
                  </PermissionGate>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Floating Action Button - Create Bill */}
      {isAuthenticated && (
        <button
          onClick={handleOpenModal}
          style={styles.fab}
          title="Create New Bill"
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "scale(1.1)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
          }}
        >
          +
        </button>
      )}

      {/* Create Bill Modal - Sale screen style (responsive) */}
      {isModalOpen && (
        <div style={{ ...styles.modalOverlay, alignItems: isMobile ? "flex-start" : "center" }} onClick={() => handleCloseModal(false)}>
          <div style={{ ...styles.modalContent, maxHeight: isMobile ? "100vh" : "90vh", borderRadius: isMobile ? 0 : 8 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ ...styles.modalHeader, paddingBottom: 12, borderBottom: "1px solid #e9edef" }}>
              <h2 style={{ ...styles.modalTitle, fontSize: isMobile ? 18 : 20 }}>{editingBillId ? "Edit Order" : "Sale"}</h2>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ display: "flex", borderRadius: 8, overflow: "hidden", border: "1px solid #e9edef" }}>
                  <button
                    type="button"
                    onClick={() => setFormData((p) => ({ ...p, status: "pending" }))}
                    style={{
                      padding: "6px 14px",
                      border: "none",
                      background: formData.status === "pending" ? "#e9edef" : "#fff",
                      color: formData.status === "pending" ? "#111b21" : "#667781",
                      fontWeight: 500,
                      fontSize: 13,
                      cursor: "pointer",
                    }}
                  >
                    Credit
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData((p) => ({ ...p, status: "paid" }))}
                    style={{
                      padding: "6px 14px",
                      border: "none",
                      background: formData.status === "paid" ? "#25d366" : "#fff",
                      color: formData.status === "paid" ? "#fff" : "#667781",
                      fontWeight: 500,
                      fontSize: 13,
                      cursor: "pointer",
                    }}
                  >
                    Cash
                  </button>
                </div>
                <button onClick={() => handleCloseModal(false)} style={styles.modalCloseButton}>×</button>
              </div>
            </div>

            <div style={{ flex: 1, overflowY: "auto", padding: isMobile ? 16 : 20 }}>
              {/* Step 1: Create bill – Customer, Date, Bill No, Bill Type, GST Type (if Bill), Notes */}
              {createStep === 1 && (
                <>
                  <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr 1fr", gap: 12, marginBottom: 16 }}>
                    <div>
                      <label style={{ ...styles.label, fontSize: 12 }}>Invoice No.</label>
                      <input
                        type="text"
                        value={formData.billNumber}
                        onChange={(e) => setFormData((p) => ({ ...p, billNumber: e.target.value }))}
                        placeholder="Auto next number"
                        style={styles.input}
                      />
                    </div>
                    <div>
                      <label style={{ ...styles.label, fontSize: 12 }}>Date</label>
                      <input
                        type="date"
                        value={formData.billDate}
                        onChange={(e) => setFormData((p) => ({ ...p, billDate: e.target.value }))}
                        style={styles.input}
                      />
                    </div>
                  </div>
                  <div style={{ marginBottom: 16, position: "relative" }}>
                    <label style={{ ...styles.label, fontSize: 12 }}>Customer (Optional)</label>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                      <input
                        type="text"
                        value={customerSearch}
                        onChange={(e) => {
                          setCustomerSearch(e.target.value);
                          if (!e.target.value) setFormData((p) => ({ ...p, partyId: "", billingName: "" }));
                          setShowPartyDropdown(true);
                        }}
                        onFocus={() => setShowPartyDropdown(true)}
                        onBlur={() => setTimeout(() => setShowPartyDropdown(false), 200)}
                        placeholder="Search customer..."
                        style={{ ...styles.input, flex: 1, minWidth: 120 }}
                      />
                      {selectedParty && (
                        <span style={{ fontSize: 13, color: "#667781", fontWeight: 500 }}>
                          Party Balance: ₹ {((selectedParty.totalDue ?? 0) - (selectedParty.totalPaid ?? 0)).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                        </span>
                      )}
                    </div>
                    {showPartyDropdown && (
                      <div
                        style={{
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          right: 0,
                          maxHeight: 200,
                          overflowY: "auto",
                          background: "#fff",
                          border: "1px solid #e9edef",
                          borderRadius: 8,
                          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                          zIndex: 10,
                        }}
                      >
                        {filteredParties.map((p) => (
                          <div
                            key={p._id}
                            onMouseDown={selectDropdownItem(() => handlePartyChange(p._id))}
                            style={{ padding: "10px 12px", cursor: "pointer", borderBottom: "1px solid #f0f2f5", fontSize: 14 }}
                          >
                            {p.partyName || p.companyName} – {p.mobileNumber}
                          </div>
                        ))}
                        {filteredParties.length === 0 && <div style={{ padding: 12, color: "#667781", fontSize: 13 }}>No customers found</div>}
                      </div>
                    )}
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ ...styles.label, fontSize: 12 }}>Billing Name (Optional)</label>
                    <input
                      type="text"
                      value={formData.billingName}
                      onChange={(e) => setFormData((p) => ({ ...p, billingName: e.target.value }))}
                      placeholder="Same as customer or enter different"
                      style={styles.input}
                    />
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ ...styles.label, fontSize: 12 }}>Bill Type</label>
                    <select
                      value={formData.billType === "kachuBill" ? "kachuBill" : "bill"}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === "kachuBill") {
                          setFormData((p) => ({ ...p, billType: "kachuBill" }));
                          setSelectedGstRate(0);
} else {
        setFormData((p) => ({ ...p, billType: "withoutTax" }));
        setSelectedGstRate(18);
      }
                      }}
                      style={styles.select}
                    >
                      <option value="kachuBill">Kachu Bill</option>
                      <option value="bill">Bill</option>
                    </select>
                  </div>
                  {(formData.billType === "withoutTax" || formData.billType === "withoutTaxIgst") && (
                    <div style={{ marginBottom: 16, display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end" }}>
                      <div style={{ flex: 1, minWidth: 140 }}>
                        <label style={{ ...styles.label, fontSize: 12 }}>GST type</label>
                        <select
                          value={formData.billType === "withoutTaxIgst" ? "igst" : "gst"}
                          onChange={(e) => {
                            const igst = e.target.value === "igst";
                            setFormData((p) => ({ ...p, billType: igst ? "withoutTaxIgst" : "withoutTax" }));
                          }}
                          style={styles.select}
                        >
                          <option value="gst">GST (CGST+SGST)</option>
                          <option value="igst">IGST</option>
                        </select>
                      </div>
                      <div style={{ flex: 1, minWidth: 100 }}>
                        <label style={{ ...styles.label, fontSize: 12 }}>GST %</label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          value={selectedGstRate === 0 ? "" : selectedGstRate}
                          onChange={(e) => setSelectedGstRate(e.target.value === "" ? 0 : parseFloat(e.target.value) || 0)}
                          style={styles.input}
                          placeholder="e.g. 5, 12, 18"
                        />
                      </div>
                      <p style={{ fontSize: 11, color: "#667781", width: "100%", margin: 0 }}>Default GST % applies to new lines; picking a product fills GST from the product. Rate + GST (e.g. 100 + 18% = ₹118).</p>
                    </div>
                  )}
                  <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
                    <button
                      type="button"
                      onClick={() => setCreateStep(2)}
                      disabled={!formData.partyId}
                      style={{ ...styles.modalButton, ...styles.modalSaveButton }}
                    >
                      Next: Add Items
                    </button>
                  </div>
                </>
              )}

              {/* Step 2: Add Items */}
              {createStep === 2 && (
                <>
                  <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                    <button type="button" onClick={() => setCreateStep(1)} style={{ ...styles.modalButton, background: "#fff", color: "#111b21", border: "1px solid #e9edef", padding: "8px 12px" }}>
                      ← Back
                    </button>
                    <span style={{ fontSize: 13, color: "#667781" }}>
                      {formData.billType === "kachuBill" ? "Kachu Bill" : `Bill (${formData.billType === "withoutTaxIgst" ? "IGST" : "GST"} ${selectedGstRate}%)`}
                    </span>
                  </div>

              {/* Add Items: 1) Select product 2) Qty, Rate, GST 3) Add — list below */}
              <div style={styles.itemsSection}>
                <label style={styles.label}>Add product to bill</label>
                <div style={{ marginBottom: 12, position: "relative" }}>
                  <label style={{ fontSize: 11, color: "#667781", display: "block", marginBottom: 4 }}>1. Select product</label>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <input
                      ref={productInputRef}
                      type="text"
                      value={productSearch}
                      onChange={(e) => {
                        setProductSearch(e.target.value);
                        if (!e.target.value) setSelectedProductId("");
                        setShowProductDropdown(true);
                      }}
                      onFocus={() => setShowProductDropdown(true)}
                      onBlur={() => setTimeout(() => setShowProductDropdown(false), 200)}
                      placeholder={selectedProductId ? "Selected — change below" : "Search or type product name..."}
                      style={{ ...styles.input, flex: 1, minWidth: 180 }}
                    />
                    {selectedProductId && (
                      <button
                        type="button"
                        onClick={() => { setSelectedProductId(""); setProductSearch(""); setShowProductDropdown(true); productInputRef.current?.focus(); }}
                        style={{ fontSize: 12, color: "#0d6efd", background: "none", border: "none", cursor: "pointer", padding: "4px 8px" }}
                      >
                        Change product
                      </button>
                    )}
                  </div>
                  {showProductDropdown && (
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        maxHeight: 220,
                        overflowY: "auto",
                        background: "#fff",
                        border: "1px solid #e9edef",
                        borderRadius: 8,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        zIndex: 10,
                      }}
                    >
                      {filteredProducts.length === 0 ? (
                        <div style={{ padding: 12, color: "#667781", fontSize: 13 }}>No products found. Type to search or add products in Product Management.</div>
                      ) : (
                        <>
                          <div style={{ padding: "8px 12px", fontSize: 11, color: "#667781", borderBottom: "1px solid #f0f2f5" }}>Select product — set Qty, Price, or Amount (amount auto-calculates qty)</div>
                          {filteredProducts.map((p) => (
                            <div
                              key={p._id}
                              onMouseDown={selectDropdownItem(() => handleProductSelect(p._id))}
                              style={{
                                padding: "10px 12px",
                                cursor: "pointer",
                                borderBottom: "1px solid #f0f2f5",
                                fontSize: 14,
                                backgroundColor: selectedProductId === p._id ? "#e7f5ff" : undefined,
                              }}
                            >
                              {p.name} – ₹{p.price}/{p.unit} {p.hsnCode ? `(HSN: ${p.hsnCode})` : ""}
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  )}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: 8, marginTop: 8, marginBottom: 8 }}>
                  <div>
                    <label style={{ fontSize: 11, color: "#667781", display: "block", marginBottom: 4 }}>2. Quantity</label>
                    <input type="number" placeholder="1" value={selectedQuantity === 0 ? "" : selectedQuantity} onChange={(e) => setSelectedQuantity(e.target.value === "" ? 0 : (parseFloat(e.target.value) || 0))} min="0" step="0.01" style={styles.input} />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, color: "#667781", display: "block", marginBottom: 4 }}>3. Price (₹)</label>
                    <input type="number" placeholder="0" value={selectedRate === 0 ? "" : selectedRate} onChange={(e) => setSelectedRate(e.target.value === "" ? 0 : (parseFloat(e.target.value) || 0))} min="0" step="0.01" style={styles.input} />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, color: "#667781", display: "block", marginBottom: 4 }}>Amount (₹)</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      placeholder="0"
                      value={amountDraft !== null ? amountDraft : (selectedLineAmount === 0 ? "" : String(selectedLineAmount))}
                      onFocus={() => setAmountDraft(selectedLineAmount === 0 ? "" : String(selectedLineAmount))}
                      onChange={(e) => setAmountDraft(e.target.value)}
                      onBlur={() => {
                        if (amountDraft !== null) {
                          applySelectedAmount(amountDraft);
                          setAmountDraft(null);
                        }
                      }}
                      style={styles.input}
                      title="Enter final line amount — quantity updates when you leave this field"
                    />
                  </div>
                </div>
                <label style={{ fontSize: 11, color: "#667781", display: "block", marginBottom: 4 }}>4. GST</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "flex-end" }}>
                  {(formData.billType === "withoutTax" || formData.billType === "withoutTaxIgst") ? (
                    <>
                      <select
                        value={formData.billType === "withoutTaxIgst" ? "igst" : "gst"}
                        onChange={(e) => {
                          const igst = e.target.value === "igst";
                          setFormData((p) => ({ ...p, billType: igst ? "withoutTaxIgst" : "withoutTax" }));
                        }}
                        style={{ ...styles.select, minWidth: 140 }}
                      >
                        <option value="gst">GST (CGST+SGST)</option>
                        <option value="igst">IGST</option>
                      </select>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={selectedGstRate === 0 ? "" : selectedGstRate}
                        onChange={(e) => setSelectedGstRate(e.target.value === "" ? 0 : parseFloat(e.target.value) || 0)}
                        style={{ ...styles.input, width: 100, minWidth: 80 }}
                        placeholder="GST %"
                      />
                    </>
                  ) : (
                    <span style={{ fontSize: 13, color: "#667781" }}>— (Kachu Bill)</span>
                  )}
                  <button type="button" onClick={handleAddProduct} style={styles.addItemButton} disabled={!selectedProductId}>+ Add to list</button>
                </div>

                <label style={{ fontSize: 11, color: "#667781", display: "block", marginTop: 16, marginBottom: 6 }}>3. Items in this bill</label>
                {billItems.length > 0 ? (
                  <div style={{ marginTop: 4, minHeight: 280, background: "#fff" }}>
                    <table style={styles.itemsTable}>
                      <thead>
                        <tr>
                          <th style={styles.itemsTableHeader}>Sr. No.</th>
                          <th style={styles.itemsTableHeader}>Item name</th>
                          <th style={styles.itemsTableHeader}>HSN/ SAC</th>
                          <th style={styles.itemsTableHeader}>Quantity</th>
                          <th style={styles.itemsTableHeader}>Unit</th>
                          <th style={styles.itemsTableHeader}>
                            {(formData.billType === "withTax" || formData.billType === "withTaxIgst") ? "Price/ Unit (incl. GST)" : "Price/ Unit"}
                          </th>
                          <th style={styles.itemsTableHeader}>Taxable amount</th>
                          <th style={styles.itemsTableHeader}>GST</th>
                          <th style={styles.itemsTableHeader}>Amount</th>
                          <th style={styles.itemsTableHeader}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {billItems.map((item, index) => {
                          const billType = formData.billType || "withoutTax";
                          const isKachha = billType === "kachuBill" || billType === "kachuBillGstIncl" || billType === "delivery";
                          const rate = item.rate || 0;
                          const qty = item.quantity || 0;
                          const g = isKachha ? 0 : (item.gstRate || 0);
                          const itemTotal = getLineTotal({ rate, quantity: qty, gstRate: g, billType });
                          let itemTaxable, itemGst;
                          if (isKachha) {
                            itemTaxable = itemTotal;
                            itemGst = 0;
                          } else if (billType === "withTax" || billType === "withTaxIgst") {
                            itemTaxable = g > 0 ? itemTotal / (1 + g / 100) : itemTotal;
                            itemGst = itemTotal - itemTaxable;
                          } else {
                            itemTaxable = rate * qty;
                            itemGst = (itemTaxable * g) / 100;
                          }
                          const inputStyle = { width: isMobile ? "52px" : "60px", padding: "6px 8px", fontSize: 13, border: "1px solid #e9edef", borderRadius: 4 };
                          const rateInputStyle = { ...inputStyle, width: isMobile ? "64px" : "72px" };
                          return (
                            <tr key={index}>
                              <td style={styles.itemsTableCell}>{index + 1}</td>
                              <td style={styles.itemsTableCell}>{item.productName}</td>
                              <td style={styles.itemsTableCell}>{item.hsnCode || "—"}</td>
                              <td style={styles.itemsTableCell}>
                                <input type="number" min={0} step="0.01" value={qty === 0 ? "" : qty} onChange={(e) => handleUpdateItem(index, "quantity", e.target.value)} placeholder="0" style={inputStyle} />
                              </td>
                              <td style={styles.itemsTableCell}>{item.unit || "—"}</td>
                              <td style={styles.itemsTableCell}>
                                <input type="number" min={0} step="0.01" value={rate === 0 ? "" : rate} onChange={(e) => handleUpdateItem(index, "rate", e.target.value)} placeholder="0" style={rateInputStyle} />
                              </td>
                              <td style={styles.itemsTableCell}>₹{itemTaxable.toFixed(2)}</td>
                              <td style={styles.itemsTableCell}>
                                {isKachha ? "—" : `${itemGst.toFixed(2)} (${g.toFixed(1)}%)`}
                              </td>
                              <td style={styles.itemsTableCell}>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={lineAmountDrafts[index] !== undefined ? lineAmountDrafts[index] : (itemTotal === 0 ? "" : String(itemTotal))}
                                  onFocus={() => setLineAmountDrafts((prev) => ({ ...prev, [index]: itemTotal === 0 ? "" : String(itemTotal) }))}
                                  onChange={(e) => setLineAmountDrafts((prev) => ({ ...prev, [index]: e.target.value }))}
                                  onBlur={(e) => {
                                    handleUpdateItem(index, "lineAmount", e.target.value);
                                    setLineAmountDrafts((prev) => {
                                      const next = { ...prev };
                                      delete next[index];
                                      return next;
                                    });
                                  }}
                                  placeholder="0"
                                  title="Edit amount — quantity updates when you leave this field"
                                  style={{ ...inputStyle, width: isMobile ? "64px" : "72px" }}
                                />
                              </td>
                              <td style={styles.itemsTableCell}>
                                <button type="button" onClick={() => handleRemoveItem(index)} style={styles.removeItemButton} title="Remove">✕</button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr style={{ fontWeight: 600, backgroundColor: "#f8f9fa", borderTop: "2px solid #e9edef" }}>
                          <td style={styles.itemsTableCell}></td>
                          <td style={{ ...styles.itemsTableCell, textAlign: "left" }}>Total</td>
                          <td style={styles.itemsTableCell}></td>
                          <td style={{ ...styles.itemsTableCell, textAlign: "center" }}>{billItems.reduce((s, i) => s + (i.quantity || 0), 0)}</td>
                          <td style={styles.itemsTableCell}></td>
                          <td style={styles.itemsTableCell}></td>
                          <td style={styles.itemsTableCell}>₹{totals.subtotal.toFixed(2)}</td>
                          <td style={styles.itemsTableCell}>₹{totals.totalGst.toFixed(2)}</td>
                          <td style={styles.itemsTableCell}>₹{totals.grandTotal.toFixed(2)}</td>
                          <td style={styles.itemsTableCell}></td>
                        </tr>
                      </tfoot>
                    </table>
                    <div style={styles.totalsSection}>
                      <div style={styles.totalRow}><span>Subtotal:</span><span>₹{totals.subtotal.toFixed(2)}</span></div>
                      {totals.totalGst > 0 && <div style={styles.totalRow}><span>Total GST:</span><span>₹{totals.totalGst.toFixed(2)}</span></div>}
                      {totals.roundOff !== 0 && <div style={styles.totalRow}><span>Round Off:</span><span>₹{totals.roundOff.toFixed(2)}</span></div>}
                      <div style={styles.grandTotalRow}>
                        <span>Total Amount:</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={totals.grandTotal === 0 ? "" : totals.grandTotal}
                          onChange={(e) => handleGrandTotalChange(e.target.value)}
                          title="Enter bill total — quantity boxes update automatically"
                          style={{ width: 120, padding: "8px 10px", fontSize: 18, fontWeight: 600, textAlign: "right", border: "2px solid #25d366", borderRadius: 6, outline: "none" }}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <p style={{ fontSize: 13, color: "#667781", marginTop: 4, padding: "12px", background: "#f8f9fa", borderRadius: 8 }}>No items yet — select a product above, enter Qty & Rate, then click &quot;Add to list&quot;. Add more products the same way.</p>
                )}
              </div>

                </>
              )}
            </div>

            {/* Modal Actions - Step 1: Cancel only; Step 2: Cancel, Save & New, Save */}
            <div style={{ ...styles.modalActions, paddingTop: 16, borderTop: "1px solid #e9edef", gap: 12 }}>
              <button onClick={() => handleCloseModal(false)} style={{ ...styles.modalButton, ...styles.modalCancelButton }}>Cancel</button>
              {createStep === 2 && (
                <>
                  {!editingBillId && (
                    <button
                      onClick={() => handleCreateBill(true)}
                      disabled={createLoading || !formData.partyId || billItems.length === 0}
                      style={{ ...styles.modalButton, background: "#fff", color: "#111b21", border: "1px solid #e9edef" }}
                    >
                      {createLoading && saveAndNewRequested ? "..." : "Save & New"}
                    </button>
                  )}
                  <button
                    onClick={() => handleCreateBill(false)}
                    disabled={createLoading || updateLoading || !formData.partyId || billItems.length === 0}
                    style={{ ...styles.modalButton, ...styles.modalSaveButton, opacity: createLoading || updateLoading || !formData.partyId || billItems.length === 0 ? 0.5 : 1 }}
                  >
                    {updateLoading && editingBillId ? "Updating..." : createLoading ? "Saving..." : editingBillId ? "Update" : "Save"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Send to WhatsApp Modal */}
      {whatsappModal.open && whatsappModal.bill && (
        <div style={styles.modalOverlay} onClick={closeWhatsappModal}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Send bill to WhatsApp</h2>
            <p style={{ ...styles.modalMessage, marginBottom: "12px" }}>
              {whatsappModal.bill.billNumber} will be sent as PDF.
            </p>
            <div className="form-group" style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", marginBottom: "6px", fontWeight: 500 }}>Phone (10 digits)</label>
              <input
                type="text"
                value={whatsappModal.phone}
                onChange={(e) =>
                  setWhatsappModal((prev) => ({ ...prev, phone: e.target.value.replace(/\D/g, "").slice(0, 10) }))
                }
                placeholder="9876543210"
                maxLength={10}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #e9edef",
                  borderRadius: "6px",
                  fontSize: "16px",
                }}
              />
            </div>
            <div style={styles.modalActions}>
              <button onClick={closeWhatsappModal} style={{ ...styles.modalButton, ...styles.modalCancelButton }}>
                Cancel
              </button>
              <button
                onClick={handleSendWhatsAppBill}
                disabled={sendingWhatsApp || whatsappModal.phone.replace(/\D/g, "").length !== 10}
                style={{ ...styles.modalButton, backgroundColor: "#25d366", color: "#fff" }}
              >
                {sendingWhatsApp ? "Sending…" : "Send"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bill View / Download Modal - show bill as HTML (same format as PDF), Download gives PDF file */}
      {pdfPreviewModal.open && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.85)",
            zIndex: 1100,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
          }}
          onClick={closePdfPreviewModal}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "8px",
              maxWidth: "95vw",
              maxHeight: "95vh",
              width: "900px",
              height: "90vh",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: "12px 16px", borderBottom: "1px solid #e9edef", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
              <span style={{ fontWeight: 600, fontSize: "16px" }}>{pdfPreviewModal.bill?.billNumber || "Bill"}</span>
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={handleDownloadPdf}
                  style={{
                    padding: "8px 16px",
                    backgroundColor: "#198754",
                    color: "#fff",
                    border: "none",
                    borderRadius: "6px",
                    fontWeight: 500,
                    cursor: "pointer",
                  }}
                >
                  Download PDF
                </button>
                {pdfPreviewModal.blobUrl && (
                  <button
                    type="button"
                    onClick={handleOpenPdfInNewTab}
                    style={{
                      padding: "8px 16px",
                      backgroundColor: "#0d6efd",
                      color: "#fff",
                      border: "none",
                      borderRadius: "6px",
                      fontWeight: 500,
                      cursor: "pointer",
                    }}
                  >
                    Open PDF in new tab
                  </button>
                )}
                <button
                  type="button"
                  onClick={closePdfPreviewModal}
                  style={{
                    padding: "8px 16px",
                    backgroundColor: "#6c757d",
                    color: "#fff",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                  }}
                >
                  Close
                </button>
              </div>
            </div>
            <div style={{ flex: 1, minHeight: 0, overflow: "auto", background: "#fff" }}>
              {pdfPreviewModal.htmlContent ? (
                <iframe
                  ref={pdfPreviewIframeRef}
                  title="Bill view"
                  srcDoc={pdfPreviewModal.htmlContent}
                  style={{ width: "100%", border: "none", display: "block", minHeight: "900px" }}
                  sandbox="allow-same-origin"
                  onLoad={() => {
                    const resize = () => {
                      try {
                        const iframe = pdfPreviewIframeRef.current;
                        if (iframe?.contentDocument?.body) {
                          const doc = iframe.contentDocument;
                          const body = doc.body;
                          const html = doc.documentElement;
                          const h = Math.max(900, Math.max(body.scrollHeight, body.offsetHeight, html.clientHeight, html.scrollHeight, html.offsetHeight) + 40);
                          iframe.style.height = h + "px";
                        }
                      } catch (_) {}
                    };
                    resize();
                    if (typeof requestAnimationFrame !== "undefined") requestAnimationFrame(resize);
                    setTimeout(resize, 100);
                  }}
                />
              ) : (
                <div style={{ padding: "24px", textAlign: "center", color: "#666" }}>Loading bill...</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div style={styles.modalOverlay} onClick={handleDeleteCancel}>
          <div style={styles.deleteModalContent} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.deleteModalTitle}>Delete Bill</h2>
            <p style={styles.deleteModalMessage}>
              Are you sure you want to delete this bill? This action cannot be undone and the PDF
              file will also be deleted.
            </p>
            <div style={styles.deleteModalActions}>
              <button
                onClick={handleDeleteCancel}
                style={{ ...styles.modalButton, ...styles.modalCancelButton }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                style={{
                  ...styles.modalButton,
                  ...styles.deleteModalConfirmButton,
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bottom Navbar */}
      <BottomNavbar />
    </div>
  );
};

export default BillManagement;
